import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, errorMessage } from '@/lib/api';
import { formatDecimal, formatLongDate } from '@/lib/format';
import { openDialog } from '@/lib/overlays';
import { queryClient } from '@/lib/queryClient';
import { navigate, routeStore } from '@/lib/router';
import { toast, toastError } from '@/lib/toast';
import { Callout, Skeleton, Switch } from '@/ui/bits';
import { CustomFieldForm, fieldType, isChoiceType, type FieldDraft } from '@/ui/CustomFieldForm';
import { confirmDialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { MenuHead, MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import type { RecordSheet } from '@shared/record';
import { filtersParamAfterChoices } from '@shared/records';
import { BIN_DAYS } from '@shared/constants';
import type { CustomFieldDef } from '@shared/types';
import { PickRow } from './Details';
import { EditRow, useCommit, useFlash } from './edit';
import { entityOf, keys, useFields } from './queries';
import { bareUrl, hrefOf } from './util';

// The "Custom fields" box: values for the fields the team defined, and a place for anyone to add, rename or remove
// a field. A field belongs to every person (or every company), so changes here show on all of them.

function asText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/* ---------- adding and editing a field ---------- */

function openFieldForm(entity: 'people' | 'companies', field: CustomFieldDef | null) {
  const noun = entity === 'people' ? 'person' : 'company';
  const save = async (draft: FieldDraft, close: () => void) => {
    const choice = isChoiceType(draft.type);
    if (field) {
      const saved = await api.patch<CustomFieldDef>(`/fields/${field.id}`, {
        label: draft.label,
        ...(choice ? { options: draft.options, renames: draft.renames } : {}),
      });
      // A list open under the sheet and filtering on this field follows its renamed and removed choices.
      const { route } = routeStore.get();
      if (choice && route.view === entity && route.params.f) {
        navigate({ params: { f: filtersParamAfterChoices(route.params.f, saved.key, draft.renames, saved.options) } }, { replace: true });
      }
      toast(draft.label === field.label ? `${draft.label} updated` : `Field renamed to “${draft.label}”`, { icon: 'check-circle' });
    } else {
      await api.post<CustomFieldDef>('/fields', { entity, label: draft.label, type: draft.type, options: draft.options });
      toast(`Field “${draft.label}” added to every ${noun}`, { icon: 'check-circle' });
    }
    void queryClient.invalidateQueries({ queryKey: keys.fields(entity) });
    void queryClient.invalidateQueries({ queryKey: [entity === 'people' ? 'person' : 'company'] });
    close();
  };
  openDialog({
    label: field ? `Edit ${field.label}` : 'Add a custom field',
    render: (close) => (
      <CustomFieldForm
        field={field}
        noun={noun}
        title={field ? `Edit “${field.label}”` : 'Add a custom field'}
        sub={`The field shows on every ${noun} for the whole team.`}
        glyph="sliders"
        onSave={(draft) => save(draft, close)}
        onCancel={close}
      />
    ),
  });
}

/* ---------- one field's menu ---------- */

function FieldMenu({ field, sheet }: { field: CustomFieldDef; sheet: RecordSheet }) {
  const qc = useQueryClient();
  const pop = usePopover('menu');
  const entity = entityOf(sheet.type);
  const remove = async () => {
    const ok = await confirmDialog({
      title: `Remove “${field.label}”?`,
      body: `The field leaves every ${sheet.type} for the whole team and goes to the Bin with its values for ${BIN_DAYS} days.`,
      confirmLabel: 'Remove field',
      icon: 'trash',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/fields/${field.id}`);
      const refresh = () => {
        void qc.invalidateQueries({ queryKey: keys.fields(entity) });
        void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      };
      refresh();
      toast(`“${field.label}” moved to the Bin`, {
        icon: 'trash',
        undo: () => {
          api
            .post('/bin/restore', { type: 'field', ids: [field.id] })
            .then(refresh)
            .catch((err) => toastError(err));
        },
      });
    } catch (err) {
      toastError(err);
    }
  };
  return (
    <>
      <button type="button" className="icon-btn xs cf-more" aria-label={`Options for ${field.label}`} data-tip="Field options" {...pop.anchorProps}>
        <Icon name="more" />
      </button>
      <Popover state={pop} width={220} label={`Options for ${field.label}`}>
        <MenuHead>
          {field.label} · {fieldType(field.type).label}
        </MenuHead>
        <MenuItem icon="edit" onSelect={() => openFieldForm(entity, field)}>
          {isChoiceType(field.type) ? 'Rename or edit choices' : 'Rename'}
        </MenuItem>
        <MenuSep />
        <MenuItem icon="trash" danger onSelect={() => void remove()}>
          Remove field
        </MenuItem>
      </Popover>
    </>
  );
}

/* ---------- value rows ---------- */

function ChoiceRow({ field, sheet, readOnly, menu }: { field: CustomFieldDef; sheet: RecordSheet; readOnly: boolean; menu: ReactNode }) {
  const commit = useCommit(sheet);
  const pop = usePopover('menu');
  const [flashing, flash] = useFlash();
  const current = sheet.custom[field.key];
  const many = field.type === 'multiselect';
  const picked: string[] = Array.isArray(current) ? current.map(String) : current ? [String(current)] : [];
  const save = (next: unknown) =>
    commit(
      field.label,
      { body: { custom: { [field.key]: next } }, show: { custom: { ...sheet.custom, [field.key]: next } } },
      { body: { custom: { [field.key]: current ?? null } }, show: { custom: sheet.custom } },
    )
      .then(flash)
      .catch((err) => toastError(err));
  const pick = (o: string) => {
    if (!many) {
      if (picked[0] !== o) void save(o);
      return;
    }
    const next = picked.includes(o) ? picked.filter((p) => p !== o) : field.options.filter((x) => x === o || picked.includes(x));
    void save(next.length ? next : null);
  };
  return (
    <>
      <PickRow label={field.label} pop={pop} readOnly={readOnly} flashing={flashing} extra={menu}>
        {picked.length ? (
          many ? (
            <span className="tags cf-tags">
              {picked.map((p) => (
                <span className="tag" key={p}>
                  {p}
                </span>
              ))}
            </span>
          ) : (
            picked[0]
          )
        ) : (
          <span className="fv-empty">{readOnly ? 'None' : many ? 'Choose some' : 'Choose one'}</span>
        )}
      </PickRow>
      <Popover state={pop} width={240} align="left" label={field.label}>
        <MenuHead>{field.label}</MenuHead>
        {field.options.map((o) => (
          <MenuItem key={o} lead={<span className={['cf-opt', many ? 'many' : '', picked.includes(o) ? 'on' : ''].filter(Boolean).join(' ')} />} checked={picked.includes(o)} keep={many} onSelect={() => pick(o)}>
            {o}
          </MenuItem>
        ))}
        {picked.length ? (
          <>
            <MenuSep />
            <MenuItem icon="x" onSelect={() => void save(null)}>
              Clear
            </MenuItem>
          </>
        ) : null}
      </Popover>
    </>
  );
}

function CheckRow({ field, sheet, readOnly, menu }: { field: CustomFieldDef; sheet: RecordSheet; readOnly: boolean; menu: ReactNode }) {
  const commit = useCommit(sheet);
  const [flashing, flash] = useFlash();
  const current = sheet.custom[field.key];
  const on = current === true || current === 'true';
  const toggle = (next: boolean) =>
    commit(
      field.label,
      { body: { custom: { [field.key]: next } }, show: { custom: { ...sheet.custom, [field.key]: next } } },
      { body: { custom: { [field.key]: current ?? null } }, show: { custom: sheet.custom } },
    )
      .then(flash)
      .catch((err) => toastError(err));
  return (
    <div className={['frow', flashing ? 'flash' : ''].filter(Boolean).join(' ')}>
      <span className="fk">{field.label}</span>
      <span className="fv cf-check">
        <span>{current === null || current === undefined ? <span className="fv-empty">Not set</span> : on ? 'Yes' : 'No'}</span>
        <Switch checked={on} onChange={(v) => void toggle(v)} label={field.label} disabled={readOnly} />
        {menu}
      </span>
    </div>
  );
}

function ValueRow({ field, sheet, readOnly }: { field: CustomFieldDef; sheet: RecordSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const menu = readOnly ? null : <FieldMenu field={field} sheet={sheet} />;
  if (isChoiceType(field.type)) return <ChoiceRow field={field} sheet={sheet} readOnly={readOnly} menu={menu} />;
  if (field.type === 'checkbox') return <CheckRow field={field} sheet={sheet} readOnly={readOnly} menu={menu} />;
  const current = sheet.custom[field.key];
  const text = asText(current);
  const display =
    field.type === 'url' && text ? (
      <a className="link" href={hrefOf(text)} target="_blank" rel="noreferrer">
        {bareUrl(text)}
      </a>
    ) : field.type === 'number' && typeof current === 'number' ? (
      formatDecimal(current)
    ) : field.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(text) ? (
      formatLongDate(`${text}T12:00:00`)
    ) : undefined;
  return (
    <EditRow
      label={field.label}
      field={`custom.${field.key}`}
      kind={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : field.type === 'date' ? 'date' : 'text'}
      value={text}
      display={display}
      empty={field.type === 'date' ? 'Pick a date' : 'Add a value'}
      readOnly={readOnly}
      extra={menu}
      validate={
        field.type === 'number'
          ? (next) => (next && !Number.isFinite(Number(next.replace(/[,\s]/g, ''))) ? 'Enter a number' : null)
          : undefined
      }
      save={(next) => {
        const value = !next ? null : field.type === 'number' ? Number(next.replace(/[,\s]/g, '')) : next;
        return commit(
          field.label,
          { body: { custom: { [field.key]: value } }, show: { custom: { ...sheet.custom, [field.key]: value } } },
          { body: { custom: { [field.key]: current ?? null } }, show: { custom: sheet.custom } },
        );
      }}
    />
  );
}

export function CustomFields({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const q = useFields(sheet.type);
  const entity = entityOf(sheet.type);
  const noun = sheet.type === 'person' ? 'person' : 'company';
  return (
    <section className="tile" aria-labelledby={`cf-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`cf-${sheet.id}`}>Custom fields</h3>
        <span className="cf-badge">Team defined</span>
        {readOnly || !q.data?.length ? null : (
          <button type="button" className="tile-more" onClick={() => openFieldForm(entity, null)} aria-label={`Add a custom field to every ${noun}`}>
            <Icon name="plus" />
            Field
          </button>
        )}
      </div>
      {q.isPending ? (
        <Skeleton rows={3} />
      ) : q.isError ? (
        <Callout
          tone="danger"
          title="The custom fields did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
              <Icon name="refresh" />
              Try again
            </button>
          }
        >
          {errorMessage(q.error)}
        </Callout>
      ) : q.data.length === 0 ? (
        <div className="box-empty">
          <p className="muted">Fields your team adds here show on every {noun}, such as a budget cycle or a preferred language.</p>
          {readOnly ? null : (
            <button type="button" className="btn btn-sm btn-tile" onClick={() => openFieldForm(entity, null)}>
              <Icon name="plus" />
              Add a field
            </button>
          )}
        </div>
      ) : (
        <div className="fields cf-fields">
          {q.data.map((f) => (
            <ValueRow key={f.id} field={f} sheet={sheet} readOnly={readOnly} />
          ))}
        </div>
      )}
    </section>
  );
}
